import React, { memo, useEffect, useState } from 'react';
// Removed framer-motion to eliminate mount animations on conversation open
import LazyImage from '../../LazyImage';
import { useDarkMode } from '../../../contexts/DarkModeContext';
import { apiGet } from '../../../utils/api';

// 🚀 DISPLAY-ONLY COMPONENT: All sanitization moved to centralized security utilities
const MessageBubble = memo(({ 
  msg, 
  user, 
  isAdminDashboard, 
  conversation, 
  onImageClick,
  renderMessageStatus,
  onReplyClick,
  replyToMessage,
  onContextMenu
}) => {
  const { isDarkMode } = useDarkMode();
  const avatarBg = isDarkMode ? '2a2a2a' : 'dddddd';
  const avatarColor = isDarkMode ? 'ffffff' : '222222';
  const isRealAvatar = (url) => {
    if (!url || typeof url !== 'string') return false;
    const u = url.trim();
    if (u.includes('ui-avatars.com') || u.includes('pravatar.cc')) return false;
    return u.startsWith('http') || u.startsWith('data:image/');
  };

  // ⚡️ SECURITY: Helper function to safely get text content
  const getTextContent = (text) => {
    if (typeof text === 'string') return text;
    if (text && typeof text === 'object' && text.sanitized) return text.sanitized;
    return '';
  };

  // 🎨 REPLY CONTEXT: WhatsApp-style reply preview component
  const ReplyContext = ({ replyToMessage, isMe, isAdminMsg }) => {
    if (!replyToMessage) return null;

    // Get display name for the replied message
    const getReplySenderName = () => {
      if (replyToMessage.name) return replyToMessage.name;
      if (replyToMessage.from) {
        // Extract name from email or use email as fallback
        const email = replyToMessage.from;
        const name = email.split('@')[0];
        return name.charAt(0).toUpperCase() + name.slice(1);
      }
      return 'Unknown User';
    };

    // Get reply content preview
    const getReplyContent = () => {
      const textContent = getTextContent(replyToMessage.text);
      if (textContent) {
        return textContent.length > 50 
          ? textContent.substring(0, 50) + '...' 
          : textContent;
      }
      if (replyToMessage.type === 'image') return '📷 Image';
      if (replyToMessage.type === 'video') return '🎥 Video';
      if (replyToMessage.type === 'file') return '📎 File';
      return 'Message';
    };

    return (
      <div className="mb-2 p-2 rounded-lg bg-black bg-opacity-20 border-l-4 border-green-400">
        <div className="text-xs text-green-300 font-medium mb-1">
          {getReplySenderName()}
        </div>
        <div className={`text-xs truncate ${
          isDarkMode ? 'text-gray-300' : 'text-gray-600'
        }`}>
          {getReplyContent()}
        </div>
      </div>
    );
  };

  // ⚡️ PERFORMANCE OPTIMIZATION: Memoize expensive calculations
  // Fix: Properly identify if the current user sent this message
  // Simplified and more robust logic
  const isMe = user && (
    // Check if message is from current user by email
    (msg.from && user.email && msg.from.toLowerCase() === user.email.toLowerCase()) ||
    // Check if message is from current user by UID
    (msg.from && user.uid && msg.from === user.uid) ||
    // Check if message is from current user by fromUid field
    (msg.fromUid && user.uid && msg.fromUid === user.uid) ||
    (msg.fromUid && user.email && msg.fromUid === user.email) ||
    // Check legacy fields
    (msg.senderId && user.uid && msg.senderId === user.uid) ||
    // Admin check
    (msg.isAdmin && user.email === 'ytproudltd@gmail.com')
  );

  const isAdminMsg = msg.from === 'ytproudltd@gmail.com' || msg.name === 'YTproud Agent' || msg.from === 'admin';
  const isMeAdminDashboard = isAdminDashboard && user && user.email === 'ytproudltd@gmail.com' && (
    msg.from === user.uid || 
    msg.from === user.email || 
    msg.fromUid === user.uid || 
    msg.fromUid === user.email
  );
  const isSeller = conversation && conversation.seller && (
    msg.senderId === conversation.seller.uid ||
    msg.from === conversation.seller.uid ||
    msg.from === conversation.seller.email ||
    msg.fromUid === conversation.seller.uid ||
    msg.fromUid === conversation.seller.email
  );
  
  // ⚡️ PERFORMANCE OPTIMIZATION: Memoize avatar URL calculation
  const sellerAvatar = conversation && (conversation.seller?.avatar || conversation.seller_avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(conversation.seller?.name || conversation.seller_name || 'Seller')}&background=${avatarBg}&color=${avatarColor}&rounded=true`);
  
  // Get the correct avatar for the message sender
  const [resolvedOtherAvatar, setResolvedOtherAvatar] = useState(null);
  const getMessageSenderAvatar = () => {
    let avatarUrl = '';
    
    // Check if this is an admin/agent message
    if (isAdminMsg || isMeAdminDashboard) {
      // Use YTProud logo for admin messages
      avatarUrl = '/images/ytproud.png';
    } else if (isMe) {
      // Current user's message - use their info
      const userAvatar = user?.avatar || user?.photoURL || '';
      avatarUrl = isRealAvatar(userAvatar)
        ? userAvatar
        : `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.username || user?.email?.split('@')[0] || 'User')}&background=${avatarBg}&color=${avatarColor}&rounded=true`;
    } else if (isSeller) {
      // Seller's message - use seller info
      const sellerAv = resolvedOtherAvatar || conversation.seller?.avatar || conversation.seller_avatar || '';
      avatarUrl = isRealAvatar(sellerAv)
        ? sellerAv
        : `https://ui-avatars.com/api/?name=${encodeURIComponent(conversation.seller?.name || conversation.seller_name || conversation.seller?.email?.split('@')[0] || 'Seller')}&background=${avatarBg}&color=${avatarColor}&rounded=true`;
    } else {
      // Buyer's message - use buyer info
      const buyerAv = resolvedOtherAvatar || conversation.buyer?.avatar || conversation.buyer_avatar || '';
      avatarUrl = isRealAvatar(buyerAv)
        ? buyerAv
        : `https://ui-avatars.com/api/?name=${encodeURIComponent(conversation.buyer?.name || conversation.buyer_name || conversation.buyer?.email?.split('@')[0] || 'Buyer')}&background=${avatarBg}&color=${avatarColor}&rounded=true`;
    }
    
    return avatarUrl;
  };

  // Resolve other party avatar from backend if missing
  useEffect(() => {
    if (isMe || isAdminMsg || isMeAdminDashboard) return;
    const currentAv = isSeller
      ? (conversation.seller?.avatar || conversation.seller_avatar)
      : (conversation.buyer?.avatar || conversation.buyer_avatar);
    if (isRealAvatar(currentAv) || isRealAvatar(resolvedOtherAvatar)) return;
    const username = isSeller
      ? (conversation.seller?.username || conversation.seller_name || conversation.seller?.name || '')
      : (conversation.buyer?.username || conversation.buyer_name || conversation.buyer?.name || '');
    const uname = String(username || '').trim();
    if (!uname) return;
    let aborted = false;
    (async () => {
      try {
        const res = await apiGet(`/api/users/public/${encodeURIComponent(uname)}`);
        if (!res.ok) return;
        const data = await res.json();
        if (aborted) return;
        if (data && isRealAvatar(data.avatar)) {
          setResolvedOtherAvatar(data.avatar);
        }
      } catch (_) {}
    })();
    return () => { aborted = true; };
  }, [conversation?.seller, conversation?.buyer, isSeller, isMe, isAdminMsg, isMeAdminDashboard]);
  


  // 🎨 CONTEXT MENU: Handle right-click to show context menu
  const handleContextMenu = (e) => {
    e.preventDefault();
    if (onContextMenu) {
      onContextMenu(e, msg);
    }
  };



  // Render bubbles
  if (isMeAdminDashboard) {
    // Admin dashboard, admin's own message (right, agent style)
    return (
      <div 
        className="flex justify-end items-end gap-2 mb-2"
      >
        <div className="flex flex-col items-end max-w-[70%]">
          <div className="flex items-center gap-2 mb-1 justify-end">
            <span className="font-bold">YTproud Agent</span>
            <span className="ml-1 px-2 py-0.5 text-xs rounded text-white align-middle font-semibold" style={{ background: '#7f7f7f' }}>Agent</span>
          </div>
          {msg.type === 'image' && msg.url ? (
            <div className={`rounded-2xl overflow-hidden shadow-lg border-4 ${isDarkMode ? 'border-[#005C4B] bg-[#005C4B]' : 'border-green-500 bg-green-500'} p-1 mt-1 cursor-pointer hover:shadow-xl transition-shadow duration-200`} onClick={() => onImageClick(msg.url)}>
              <LazyImage 
                src={msg.url} 
                alt="shared" 
                className="max-w-[280px] sm:max-w-[320px] md:max-w-[360px] lg:max-w-[400px] w-full h-auto object-cover rounded-2xl" 
              />
            </div>
          ) : msg.type === 'video' && msg.url ? (
            <video src={msg.url} controls className="max-w-[280px] sm:max-w-[320px] md:max-w-[360px] lg:max-w-[400px] max-h-60 rounded-xl mt-1 border-2 border-green-500" />
          ) : (
            <div className={`${
              isDarkMode 
                ? 'bg-[#005C4B] text-white shadow-xl' // WhatsApp dark mode sent message color for admin
                : 'bg-black text-white shadow-lg'
            } rounded-2xl rounded-br-sm px-5 py-3 border-2 font-semibold break-words whitespace-pre-line max-w-[90vw] sm:max-w-[70vw] md:max-w-lg lg:max-xl relative cursor-pointer ${
              msg.isTampered ? 'border-red-500 bg-red-900' : 
              msg.isPending ? 'border-yellow-400 bg-gray-800' : 'border-purple-500'
            }`} onContextMenu={handleContextMenu}>
              <div className="flex items-end justify-between gap-2">
                <div className="flex-1">
                  {getTextContent(msg.text)}
                  {msg.isTampered && (
                    <div className="text-xs text-red-300 mt-1">
                      ⚠️ Message integrity compromised
                    </div>
                  )}
                </div>
                
                {/* ⚡️ WHATSAPP STYLE: Time and status on same line as text */}
                <div className={`flex items-center gap-1 text-xs flex-shrink-0 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-600'
                }`}>
                  <span>{msg.time}</span>
                  {renderMessageStatus(msg)}
                  {msg.status === 'sending' && (
                    <span className="flex items-center gap-1 ml-1">
                      <div className={`w-2 h-2 rounded-full animate-pulse ${
                        isDarkMode ? 'bg-gray-400' : 'bg-gray-500'
                      }`}></div>
                      sending...
                    </span>
                  )}
                  {msg.status === 'failed' && (
                    <span className="flex items-center gap-1 ml-1 text-red-400">
                      <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                      failed
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        <img
          src={getMessageSenderAvatar()}
          alt="YTproud Agent"
          className="w-8 h-8 rounded-full object-cover shadow"
        />
      </div>
    );
  } else if (isMe) {
    // User's own message (right, red bubble)
    return (
      <div 
        className="flex justify-end items-end gap-2 mb-2"
      >
        <div className="flex flex-col items-end max-w-[70%]">
          {msg.type === 'image' && msg.url ? (
            <div className={`rounded-2xl overflow-hidden shadow-lg border-4 ${isDarkMode ? 'border-[#005C4B] bg-[#005C4B]' : 'border-green-500 bg-green-500'} p-1 mt-1 cursor-pointer hover:shadow-xl transition-shadow duration-200`} onClick={() => onImageClick(msg.url)}>
              <LazyImage 
                src={msg.url} 
                alt="shared" 
                className="max-w-[280px] sm:max-w-[320px] md:max-w-[360px] lg:max-w-[400px] w-full h-auto object-cover rounded-2xl" 
              />
            </div>
          ) : msg.type === 'video' && msg.url ? (
            <video src={msg.url} controls className={`max-w-[280px] sm:max-w-[320px] md:max-w-[360px] lg:max-w-[400px] max-h-60 rounded-xl mt-1 border-4 ${isDarkMode ? 'border-[#005C4B] bg-[#005C4B]' : 'border-green-500 bg-green-500'}`} />
          ) : (
            <div 
              className={`${
                isDarkMode 
                  ? 'bg-[#005C4B] text-white shadow-lg' // WhatsApp dark mode sent message color
                  : 'bg-gradient-to-r from-red-500 to-red-600 text-white shadow-md'
              } rounded-2xl rounded-br-sm px-5 py-3 text-base break-words whitespace-pre-line max-w-[90vw] sm:max-w-[70vw] md:max-w-lg lg:max-w-xl relative cursor-pointer`}
              onContextMenu={handleContextMenu}
            >
              {/* 🎨 REPLY CONTEXT: Show reply information */}
              {msg.replyTo && (
                <ReplyContext 
                  replyToMessage={msg.replyTo} 
                  isMe={isMe} 
                  isAdminMsg={isAdminMsg} 
                />
              )}
              <div className="flex items-end justify-between gap-2">
                <div className="flex-1">
                  {getTextContent(msg.text)}
                </div>
                
                {/* ⚡️ WHATSAPP STYLE: Time and status on same line as text */}
                <div className={`flex items-center gap-1 text-xs flex-shrink-0 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  <span>{msg.time}</span>
                  {renderMessageStatus(msg)}
                  {msg.status === 'sending' && (
                    <span className="flex items-center gap-1 ml-1">
                      <div className={`w-2 h-2 rounded-full animate-pulse ${
                        isDarkMode ? 'bg-gray-300' : 'bg-gray-400'
                      }`}></div>
                      sending...
                    </span>
                  )}
                  {msg.status === 'failed' && (
                    <span className="flex items-center gap-1 ml-1 text-red-300">
                      <div className="w-2 h-2 bg-red-300 rounded-full"></div>
                      failed
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        <img
          src={getMessageSenderAvatar()}
          alt="Me"
          className="w-8 h-8 rounded-full object-cover shadow"
        />
      </div>
    );
  } else if (isAdminMsg) {
    // Agent/admin message (left, agent style)
    return (
      <div 
        className="flex items-end gap-2 mb-2"
      >
        <img
          src={getMessageSenderAvatar()}
          alt="YTproud Agent"
          className="w-8 h-8 rounded-full object-cover shadow"
        />
        <div className="flex flex-col items-start max-w-[70%]">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-bold">YTproud Agent</span>
            <span className="ml-1 px-2 py-0.5 text-xs rounded text-white align-middle font-semibold" style={{ background: '#7f7f7f' }}>Agent</span>
          </div>
          {msg.type === 'image' && msg.url ? (
            <div className={`rounded-2xl overflow-hidden shadow-lg border-4 ${isDarkMode ? 'border-[#202C33] bg-[#202C33]' : 'border-blue-500 bg-blue-500'} p-1 mt-1 cursor-pointer hover:shadow-xl transition-shadow duration-200`} onClick={() => onImageClick(msg.url)}>
              <LazyImage 
                src={msg.url} 
                alt="shared" 
                className="max-w-[280px] sm:max-w-[320px] md:max-w-[360px] lg:max-w-[400px] w-full h-auto object-cover rounded-2xl" 
              />
            </div>
          ) : msg.type === 'video' && msg.url ? (
            <video src={msg.url} controls className="max-w-[280px] sm:max-w-[320px] md:max-w-[360px] lg:max-w-[400px] max-h-60 rounded-xl mt-1 border-2 border-blue-500" />
          ) : (
            <div 
              className={`${
                isDarkMode 
                  ? 'bg-[#202C33] text-white shadow-xl' // WhatsApp dark mode received message color for admin
                  : 'bg-black text-white shadow-lg'
              } rounded-2xl rounded-bl-sm px-5 py-3 border-2 border-purple-500 font-semibold break-words whitespace-pre-line max-w-[90vw] sm:max-w-[70vw] md:max-w-lg lg:max-w-xl relative cursor-pointer`}
              onContextMenu={handleContextMenu}
            >
              {/* 🎨 REPLY CONTEXT: Show reply information */}
              {msg.replyTo && (
                <ReplyContext 
                  replyToMessage={msg.replyTo} 
                  isMe={isMe} 
                  isAdminMsg={isAdminMsg} 
                />
              )}
              <div className="flex items-end justify-between gap-2">
                <div className="flex-1">
                  {getTextContent(msg.text)}
                </div>
                
                {/* ⚡️ WHATSAPP STYLE: Time and status on same line as text */}
                <div className={`flex items-center gap-1 text-xs flex-shrink-0 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-300'
                }`}>
                  <span>{msg.time}</span>
                  {renderMessageStatus(msg)}
                  {msg.status === 'sending' && (
                    <span className="flex items-center gap-1 ml-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse"></div>
                      sending...
                    </span>
                  )}
                  {msg.status === 'failed' && (
                    <span className="flex items-center gap-1 ml-1 text-red-400">
                      <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                      failed
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  } else {
    // Other user message (left, blue bubble)
    return (
      <div 
        className="flex items-end gap-2 mb-2"
      >
        <img
          src={getMessageSenderAvatar()}
          alt={msg.name || "User"}
          className="w-8 h-8 rounded-full object-cover shadow"
        />
        <div className="flex flex-col items-start max-w-[70%]">
          {msg.type === 'image' && msg.url ? (
            <div className={`rounded-2xl overflow-hidden shadow-lg border-4 ${isDarkMode ? 'border-[#202C33] bg-[#202C33]' : 'border-blue-500 bg-blue-500'} p-1 mt-1 cursor-pointer hover:shadow-xl transition-shadow duration-200`} onClick={() => onImageClick(msg.url)}>
              <LazyImage 
                src={msg.url} 
                alt="shared" 
                className="max-w-[280px] sm:max-w-[320px] md:max-w-[360px] lg:max-w-[400px] w-full h-auto object-cover rounded-2xl" 
              />
            </div>
          ) : msg.type === 'video' && msg.url ? (
            <video src={msg.url} controls className={`max-w-[280px] sm:max-w-[320px] md:max-w-[360px] lg:max-w-[400px] max-h-60 rounded-xl mt-1 border-4 ${isDarkMode ? 'border-[#202C33] bg-[#202C33]' : 'border-blue-500 bg-blue-500'}`} />
          ) : (
            <div 
              className={`${
                isDarkMode 
                  ? 'bg-[#202C33] text-white shadow-lg' // WhatsApp dark mode received message color
                  : 'bg-gradient-to-r from-blue-400 to-blue-600 text-white shadow'
              } rounded-2xl rounded-bl-sm px-5 py-3 text-base break-words whitespace-pre-line max-w-[90vw] sm:max-w-[70vw] md:max-w-lg lg:max-w-xl relative cursor-pointer`}
              onContextMenu={handleContextMenu}
            >
              {/* 🎨 REPLY CONTEXT: Show reply information */}
              {msg.replyTo && (
                <ReplyContext 
                  replyToMessage={msg.replyTo} 
                  isMe={isMe} 
                  isAdminMsg={isAdminMsg} 
                />
              )}
              <div className="flex items-end justify-between gap-2">
                <div className="flex-1">
                  {getTextContent(msg.text)}
                </div>
                
                {/* ⚡️ WHATSAPP STYLE: Time and status on same line as text */}
                <div className={`flex items-center gap-1 text-xs flex-shrink-0 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-200'
                }`}>
                  <span>{msg.time}</span>
                  {renderMessageStatus(msg)}
                  {msg.status === 'sending' && (
                    <span className="flex items-center gap-1 ml-1">
                      <div className="w-2 h-2 bg-gray-300 rounded-full animate-pulse"></div>
                      sending...
                    </span>
                  )}
                  {msg.status === 'failed' && (
                    <span className="flex items-center gap-1 ml-1 text-red-300">
                      <div className="w-2 h-2 bg-red-300 rounded-full"></div>
                      failed
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
});

MessageBubble.displayName = 'MessageBubble';

export default MessageBubble;
